import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faChartBar,
    faCloud,
    faShieldAlt,
    faLock,
    faUsers,
    faStar,
} from "@fortawesome/free-solid-svg-icons";

/* ==========================================================================
   STATIC / FALLBACK DATA CONFIGURATION
   --------------------------------------------------------------------------
   These fallback values are used when dynamic window globals or props are
   not available. They contain only category metadata with NO mock scores.
   ========================================================================== */

/** STATIC FALLBACK: Default list of categories with fallback colors */
const DEFAULT_CATEGORIES = [
    {
        category_id: 1,
        category_name: "Safety & Compliance",
        color: "#FF5A00",
    },
    {
        category_id: 2,
        category_name: "Incident Response",
        color: "#DF1995",
    },
    {
        category_id: 3,
        category_name: "Data Privacy",
        color: "#8031A7",
    },
    {
        category_id: 4,
        category_name: "Cyber Security",
        color: "#00A3E0",
    },
    {
        category_id: 5,
        category_name: "Risk Management",
        color: "#00BFB3",
    },
    {
        category_id: 6,
        category_name: "Leadership & Culture",
        color: "#FDD100",
    },
];

/** STATIC FALLBACK: Icons mapped by index if category has no custom icon_image */
const FALLBACK_ICONS = [
    faCloud,
    faShieldAlt,
    faLock,
    faUsers,
    faStar,
    faChartBar,
];

/** STATIC FALLBACK: Title & Description defaults if window.end_page is unset */
const DEFAULT_TITLE = "Safety Score";
const DEFAULT_DESCRIPTION = "";

/* ==========================================================================
   SUB-COMPONENT: ScoreItem
   --------------------------------------------------------------------------
   Renders an individual category row with icon badge, title, score, & progress bar.
   ========================================================================== */
const ScoreItem = ({ category, index, score, isLoaded, isAnimated }) => {
    /* DYNAMIC VARIABLE: Category metadata & score */
    const {
        category_id,
        category_name = "Category Name",
        color = "#00A3E0" /* FALLBACK COLOR */,
        icon_image,
    } = category;

    /* STATIC FALLBACK: Select fallback FontAwesome icon based on row index */
    const fallbackIcon = FALLBACK_ICONS[index % FALLBACK_ICONS.length];
    const parsedScore =
        typeof score === "number"
            ? score
            : typeof score === "string" &&
                !isNaN(Number(score)) &&
                score.trim() !== ""
              ? Number(score)
              : null;

    const numericScore =
        parsedScore !== null
            ? Math.min(100, Math.max(0, Math.round(parsedScore)))
            : null;

    const isZeroScore =
        isLoaded && (numericScore === 0 || numericScore === null);
    const hasValidScore = isLoaded && numericScore !== null && numericScore > 0;

    // Staggered transition delay based on index for smooth sequential animation
    const staggerDelay = `${index * 120}ms`;

    return (
        <div
            className={`leaderboard-score-item${isZeroScore ? " is-zero" : ""}`}
            key={category_id || index}
        >
            {/* DYNAMIC STYLING: Category Badge with dynamic background color */}
            <div
                className="category-icon-badge"
                style={{ backgroundColor: color }}
            >
                {/* DYNAMIC IMAGE OR FALLBACK ICON */}
                {icon_image ? (
                    <img src={icon_image} alt={category_name} />
                ) : (
                    <FontAwesomeIcon
                        icon={fallbackIcon}
                        className="badge-icon"
                    />
                )}
            </div>

            <div className="category-score-details">
                <div className="category-score-header">
                    {/* DYNAMIC TEXT: Category Title & Percentage Score */}
                    <span className="category-name">{category_name}</span>
                    {!isZeroScore && (
                        <span
                            className="category-percentage"
                            style={{
                                opacity: hasValidScore && isAnimated ? 1 : 0,
                                transitionDelay: staggerDelay,
                            }}
                        >
                            {hasValidScore ? `${numericScore}%` : ""}
                        </span>
                    )}
                </div>

                {!isZeroScore && (
                    <div
                        className="category-progress-track"
                        style={{
                            opacity: hasValidScore && isAnimated ? 1 : 0,
                            transitionDelay: staggerDelay,
                        }}
                    >
                        {/* DYNAMIC BAR WIDTH: Transition from 0% to numericScore% */}
                        <div
                            className="category-progress-bar"
                            style={{
                                width:
                                    hasValidScore && isAnimated
                                        ? `${numericScore}%`
                                        : "0%",
                                transitionDelay: staggerDelay,
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

ScoreItem.propTypes = {
    category: PropTypes.shape({
        category_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        category_name: PropTypes.string,
        color: PropTypes.string,
        icon_image: PropTypes.string,
        score: PropTypes.number,
    }).isRequired,
    index: PropTypes.number.isRequired,
    score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isLoaded: PropTypes.bool,
    isAnimated: PropTypes.bool,
};

/* ==========================================================================
   MAIN COMPONENT: Leaderboard
   ========================================================================== */
export const Leaderboard = ({
    title: propTitle,
    description: propDescription,
    categories: propCategories,
    isLoading: propIsLoading,
    isScoringEnabled = true,
    activeRightView = "actions",
    onToggleView,
    onGoToActions,
}) => {
    /* ----------------------------------------------------------------------
       DYNAMIC VARIABLES RESOLUTION:
       Prioritize explicit Component Props -> Global Window Variables -> Static Defaults
       ---------------------------------------------------------------------- */

    /** DYNAMIC VARIABLE: Title (Props -> window.end_page.end_page_safety_title -> Default) */
    const title =
        propTitle || window.end_page?.end_page_safety_title || DEFAULT_TITLE;

    /** DYNAMIC VARIABLE: Description HTML (Props -> window.end_page.end_page_safety_description -> Default) */
    const description =
        propDescription ||
        window.end_page?.end_page_safety_description ||
        DEFAULT_DESCRIPTION;

    const nextButtonText =
        window.end_page?.end_page_next_button_text || "Go to Team Action";

    /**
     * DYNAMIC DATA SOURCE: Categories & Scores List
     * Source priority for category metadata:
     * 1. propCategories (if passed directly)
     * 2. window.categories (pre-loaded from WP backend)
     * 3. DEFAULT_CATEGORIES (fallback)
     */
    const sourceCategories =
        propCategories && propCategories.length > 0
            ? propCategories
            : window.categories && window.categories.length > 0
              ? window.categories
              : DEFAULT_CATEGORIES;

    // Check if scores have completed loading
    const isLoaded =
        propIsLoading !== undefined
            ? !propIsLoading && propCategories !== null
            : propCategories !== null && Array.isArray(propCategories);

    // Track when the wrapper slide-in animation (500ms) has completed
    const [isWrapperReady, setIsWrapperReady] = useState(false);

    useEffect(() => {
        // Fallback timer for 500ms slide-from-left animation + 50ms buffer
        const timer = setTimeout(() => {
            setIsWrapperReady(true);
        }, 550);
        return () => clearTimeout(timer);
    }, []);

    // Animation state to trigger score transitions after slide finishes and scores load
    const [isAnimated, setIsAnimated] = useState(false);

    useEffect(() => {
        if (isLoaded && isWrapperReady) {
            const timer = setTimeout(() => {
                setIsAnimated(true);
            }, 80);
            return () => clearTimeout(timer);
        } else {
            setIsAnimated(false);
        }
    }, [isLoaded, isWrapperReady]);

    // Map scores by category_id for reliable lookup
    const scoresMap = useMemo(() => {
        if (!propCategories || !Array.isArray(propCategories)) {
            return null;
        }
        const map = new Map();
        propCategories.forEach((cat) => {
            if (cat.category_id != null) {
                map.set(Number(cat.category_id), cat.score);
            }
        });
        return map;
    }, [propCategories]);

    return (
        <div
            className="leaderboard-wrapper"
            onAnimationEnd={(e) => {
                if (
                    e.target === e.currentTarget ||
                    e.animationName === "slide-from-left"
                ) {
                    setIsWrapperReady(true);
                }
            }}
        >
            {/* HEADER SECTION: Title & Description */}
            <div className="leaderboard-header">
                {/* DYNAMIC TEXT: Header Title */}
                <h2 className="leaderboard-title">{title}</h2>

                {/* DYNAMIC HTML: Rich text description from WP Admin panel */}
                {description && (
                    <div
                        className="leaderboard-description"
                        dangerouslySetInnerHTML={{ __html: description }}
                    />
                )}
            </div>

            {/* SCORES LIST CONTAINER */}
            <div className="leaderboard-scores-container">
                {sourceCategories.map((cat, idx) => {
                    const catId = Number(cat.category_id);
                    const score =
                        scoresMap && scoresMap.has(catId)
                            ? scoresMap.get(catId)
                            : cat.score !== undefined
                              ? cat.score
                              : scoresMap !== null
                                ? 0
                                : null;

                    return (
                        <ScoreItem
                            key={cat.category_id || idx}
                            category={cat}
                            index={idx}
                            score={score}
                            isLoaded={isLoaded}
                            isAnimated={isAnimated}
                        />
                    );
                })}
            </div>

            {/* FOOTER TOGGLE ACTION: Rendered ONLY if isScoringEnabled is true */}
            {isScoringEnabled && (onGoToActions || onToggleView) && (
                <div className="leaderboard-footer">
                    <button
                        className="go-to-actions-btn"
                        type="button"
                        onClick={onGoToActions || onToggleView}
                    >
                        <span>{nextButtonText}</span>
                    </button>
                </div>
            )}
        </div>
    );
};

Leaderboard.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    categories: PropTypes.arrayOf(
        PropTypes.shape({
            category_id: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]),
            category_name: PropTypes.string,
            color: PropTypes.string,
            icon_image: PropTypes.string,
            score: PropTypes.number,
        }),
    ),
    isLoading: PropTypes.bool,
    isScoringEnabled: PropTypes.bool,
    activeRightView: PropTypes.string,
    onToggleView: PropTypes.func,
    onGoToActions: PropTypes.func,
};

export default Leaderboard;
